import React, { useContext } from 'react';

import NoteBox from 'common/common/NoteBox';
import { ShowIntercomContext } from 'common/containers/IntercomContainer';
import GenerateUserHashCSharpSnippet from 'common/developers/docs/sdk/snippets/GenerateUserHashCSharpSnippet';
import GenerateUserHashGoSnippet from 'common/developers/docs/sdk/snippets/GenerateUserHashGoSnippet';
import GenerateUserHashJavaSnippet from 'common/developers/docs/sdk/snippets/GenerateUserHashJavaSnippet';
import GenerateUserHashNodeSnippet from 'common/developers/docs/sdk/snippets/GenerateUserHashNodeSnippet';
import GenerateUserHashPHPSnippet from 'common/developers/docs/sdk/snippets/GenerateUserHashPHPSnippet';
import GenerateUserHashPythonSnippet from 'common/developers/docs/sdk/snippets/GenerateUserHashPythonSnippet';
import GenerateUserHashRubySnippet from 'common/developers/docs/sdk/snippets/GenerateUserHashRubySnippet';
import IdentifyHashSnippet from 'common/developers/docs/sdk/snippets/IdentifyHashSnippet';
import MultiLanguageCodeBlock from 'common/developers/docs/widget/MultiLanguageCodeBlock';
import Helmet from 'common/helmets/Helmet';
import Tappable from 'common/Tappable';

import 'css/components/developers/docs/install/_IdentifyDocs.scss';

const SecureIdentifyDocs = () => {
  const showIntercom = useContext(ShowIntercomContext);

  const onContactUsTapped = () => {
    showIntercom();
  };

  return (
    <div className="identifyDocs">
      <Helmet
        title="Secure Identify Requests Install Docs | Canny"
        description="Install our JavaScript SDK to identify your users and ensure feedback is tied to existing user accounts in your application."
        image="https://canny.io/images/canny-social.png"
        url="https://canny.io/docs/install/security"
      />
      <h1 className="heading">Secure Identify Requests</h1>
      <p className="description">
        To enhance the security of the Canny SDK, provide a user-specific hash in your request that
        allows Canny to verify that the authentication request came from your application.
      </p>
      <ol className="numberedList">
        <li>
          <p className="stepHeading">1. Generate user hash on your&nbsp;server</p>
          <NoteBox title="Note">
            <p className="main">
              You can find your secret API key in{' '}
              <a
                href="https://canny.io/redirect?to=%2Fadmin%2Fsettings%2Fapi"
                target="_blank"
                rel="nofollow noopener"
                className="emphasize">
                your company settings
              </a>
              . This key is secret! Store it on your server and don't share&nbsp;it.
            </p>
          </NoteBox>
          <div className="contents">
            <MultiLanguageCodeBlock
              blocks={[
                {
                  name: 'Node.js',
                  language: 'js',
                  code: GenerateUserHashNodeSnippet,
                },
                {
                  name: 'C#',
                  language: 'csharp',
                  code: GenerateUserHashCSharpSnippet,
                },
                {
                  name: 'Go',
                  language: 'go',
                  code: GenerateUserHashGoSnippet,
                },
                {
                  name: 'Java',
                  language: 'java',
                  code: GenerateUserHashJavaSnippet,
                },
                {
                  name: 'PHP',
                  language: 'php',
                  code: GenerateUserHashPHPSnippet,
                },
                {
                  name: 'Python',
                  language: 'python',
                  code: GenerateUserHashPythonSnippet,
                },
                {
                  name: 'Ruby',
                  language: 'ruby',
                  code: GenerateUserHashRubySnippet,
                },
              ]}
            />
          </div>
        </li>
        <li>
          <p className="subheading">2. Send user hash to Canny when identifying users</p>
          <div className="contents">
            <MultiLanguageCodeBlock
              blocks={[
                {
                  language: 'js',
                  code: IdentifyHashSnippet,
                },
              ]}
              showSelector={false}
            />
          </div>
        </li>
        <li>
          <p className="subheading">
            3. Enable setting in your{' '}
            <a
              href="https://canny.io/redirect?to=%2Fadmin%2Fsettings%2Fsecurity"
              target="_blank"
              rel="nofollow noopener"
              className="emphasize">
              company security settings
            </a>
          </p>
          <p className="description">
            Once enabled, any requests without valid hashes will be rejected, ensuring that only
            verified users can authenticate. This blocks malicious actors from impersonating your
            users.
          </p>
        </li>
        <li>
          <p className="subheading">4. Verify that your identify request works as expected</p>
          <p className="description">
            Trigger the identify request by visiting your app and use your browser's dev tools to
            ensure that the Canny SDK isn't showing any errors in the console. If you see a "Canny:
            Skipping identify request" message, try clearing your site data or using a
            private/incognito window. If you run into an error that you can't figure out,{' '}
            <Tappable onTap={onContactUsTapped}>
              <span className="contact">contact us</span>
            </Tappable>
          </p>
        </li>
      </ol>
    </div>
  );
};

export default SecureIdentifyDocs;
